import {ContentContainer} from '@coolcolduk/ui-mui';
import {filterArrayUndefined} from '@coolcolduk/util';
import {Alert, Box, Button, CircularProgress, Typography} from '@mui/material';
import {useState} from 'react';
import {ExamModule, UserModule} from '@cp/model';
import PageHeader from '../../../components/PageHeader';
import {ProtectedRouteChildrenProps} from '../../../components/template/ProtectedRoute';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import useRemoveSelectedModules from '../../../hooks/users/useRemoveSelectedModules';
import useUpdateSelectedModule from '../../../hooks/users/useUpdateSelectedModule';
import AddModuleDialog from './AddModuleDialog';
import DeleteModuleDialog from './DeleteModuleDialog';
import ModuleDetailDialog from './ModuleDetailDialog';
import ModuleSettingsCard from './ModuleSettingsCard';
import ModuleSettingsEmptyCard from './ModuleSettingsEmptyCard';

export default function ModuleSettingsPage({user}: ProtectedRouteChildrenProps) {
  const [selectedModule, setSelectedModule] = useState<ExamModule | null>(null);
  const [selectedUserModule, setSelectedUserModule] = useState<UserModule | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addModuleDialogOpen, setAddModuleDialogOpen] = useState(false);
  const [deleteModuleDialogOpen, setDeleteModuleDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<{module: ExamModule; userModule: UserModule} | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {examModules, loading: modulesLoading, error: modulesError} = useListExamModules();

  const {updateSelectedModule, loading: saveLoading} = useUpdateSelectedModule();
  const {removeSelectedModules, loading: removeLoading} = useRemoveSelectedModules();

  const handleViewModule = (module: ExamModule, userModule: UserModule) => {
    setSelectedModule(module);
    setSelectedUserModule(userModule);
    setDialogOpen(true);
  };

  const handleSaveTopics = async (topics: string[]) => {
    if (!selectedUserModule || !selectedModule) return;

    setError(null);

    try {
      // Update the user module with new topics
      await updateSelectedModule(selectedModule.id, topics);
      setDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save topics');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedModule(null);
    setSelectedUserModule(null);
  };

  const handleAddModule = () => {
    setAddModuleDialogOpen(true);
  };

  const handleCloseAddModuleDialog = () => {
    setAddModuleDialogOpen(false);
  };

  const handleDeleteModule = (module: ExamModule, userModule: UserModule) => {
    setModuleToDelete({module, userModule});
    setDeleteModuleDialogOpen(true);
  };

  const handleConfirmDeleteModule = async () => {
    if (!moduleToDelete) return;

    setError(null);

    try {
      // Remove the module using the dedicated removeSelectedModules mutation
      await removeSelectedModules(moduleToDelete.module.id);
      setDeleteModuleDialogOpen(false);
      setModuleToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to remove module');
    }
  };

  const handleCloseDeleteModuleDialog = () => {
    setDeleteModuleDialogOpen(false);
    setModuleToDelete(null);
  };

  // Get user's selected modules with full module details
  const userSelectedModules = filterArrayUndefined(
    (user.selectedModules || []).map((userModule) => {
      const fullModule = examModules.find((m) => m.id === userModule.moduleId);
      if (!fullModule) return undefined;
      return {
        userModule,
        fullModule,
      };
    }),
  );

  // Get user's selected module IDs for filtering available modules
  const userSelectedModuleIds = (user.selectedModules || []).map((userModule) => userModule.moduleId);

  if (modulesLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh'}}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (modulesError) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">Failed to load modules. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <ContentContainer data-testid="module-settings-page">
      <PageHeader title="Module Settings" />

      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography variant="body1" color="text.secondary">
            Manage your selected study modules and topics. Click the view icon to customize which topics you want to
            study.
          </Typography>
          <Button
            variant="contained"
            onClick={handleAddModule}
            sx={{bgcolor: '#1B263B', '&:hover': {bgcolor: '#1B263B', opacity: 0.8}}}
            data-testid="add-module-button"
          >
            Add Module
          </Button>
        </Box>

        {userSelectedModules.length === 0 ? (
          <ModuleSettingsEmptyCard onAddModule={handleAddModule} />
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            {userSelectedModules.map(({userModule, fullModule}) => (
              <ModuleSettingsCard
                key={userModule.moduleId}
                userModule={userModule}
                fullModule={fullModule}
                handleViewModule={handleViewModule}
                handleDeleteModule={handleDeleteModule}
              />
            ))}
          </Box>
        )}
      </Box>

      <ModuleDetailDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        module={selectedModule}
        userModule={selectedUserModule}
        onSave={handleSaveTopics}
        loading={saveLoading}
      />

      <AddModuleDialog
        open={addModuleDialogOpen}
        onClose={handleCloseAddModuleDialog}
        availableModules={examModules}
        userSelectedModuleIds={userSelectedModuleIds}
      />

      <DeleteModuleDialog
        open={deleteModuleDialogOpen}
        onClose={handleCloseDeleteModuleDialog}
        onConfirm={handleConfirmDeleteModule}
        module={moduleToDelete?.module || null}
        userModule={moduleToDelete?.userModule || null}
        loading={removeLoading}
      />
    </ContentContainer>
  );
}
